import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, LoadingState, EmptyState } from '../components/ui';
import { alert } from '../utils/toast';
import type { SupportTicket } from '../types';
import {
  LifeBuoy,
  ArrowLeft,
  Clock,
  User,
  ShieldCheck,
  Send,
  CheckCircle2,
  Building2,
} from 'lucide-react';

export const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [response, setResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: async () => {
      const res = await api.get<any>(`/admin/support/tickets/${id}`);
      return (res.data?.data || res.data) as SupportTicket;
    },
    enabled: !!id,
  });

  const respondMutation = useMutation({
    mutationFn: async (message: string) => {
      await api.post(`/admin/support/tickets/${id}/messages`, { message });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
      setResponse('');
      void alert.success('Resposta enviada com sucesso!');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      await api.put(`/admin/support/tickets/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
      setNewStatus('');
      void alert.success('Estado atualizado com sucesso!');
    },
  });

  const handleRespond = (e: React.FormEvent) => {
    e.preventDefault();
    if (response.trim()) {
      respondMutation.mutate(response);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;

    const result = await alert.confirm(
      'Alterar Estado?',
      `Deseja alterar o estado do ticket para "${newStatus.replace('_', ' ')}"?`
    );

    if (result.isConfirmed) {
      updateStatusMutation.mutate(newStatus);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/support">Tickets</BreadcrumbItem>
          <BreadcrumbItem>A carregar...</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem href="/support">Tickets</BreadcrumbItem>
          <BreadcrumbItem>Não encontrado</BreadcrumbItem>
        </Breadcrumbs>
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-800 shadow-xs">
          <EmptyState
            title="Ticket não encontrado"
            description="O ticket que está a tentar aceder não existe ou foi removido."
          />
          <Link
            to="/support"
            className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-4 font-semibold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Voltar para os tickets</span>
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-rose-50 text-rose-700 border-rose-200/80 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900/50',
      in_progress:
        'bg-amber-50 text-amber-700 border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50',
      waiting_for_customer:
        'bg-sky-50 text-sky-700 border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-900/50',
      resolved:
        'bg-emerald-50 text-emerald-700 border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900/50',
      closed:
        'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    };
    return colors[status] || colors.closed;
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      critical:
        'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800 font-bold',
      high: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50',
      medium:
        'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-900/50',
      low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    };
    return colors[priority] || colors.low;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem href="/support">Tickets</BreadcrumbItem>
        <BreadcrumbItem>#{ticket.ticket_number}</BreadcrumbItem>
      </Breadcrumbs>

      <div>
        <Link
          to="/support"
          className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm mb-4 font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para a lista de tickets</span>
        </Link>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
              <LifeBuoy className="w-8 h-8 text-primary-500" />
              <span>Ticket #{ticket.ticket_number}</span>
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1 text-base">{ticket.subject}</p>
          </div>

          <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-2xs">
            <select
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-hidden"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="">Alterar Estado...</option>
              <option value="in_progress">Em Progresso</option>
              <option value="waiting_for_customer">Aguardando Cliente</option>
              <option value="resolved">Resolvido</option>
              <option value="closed">Fechado</option>
            </select>
            <button
              onClick={handleStatusChange}
              disabled={!newStatus || updateStatusMutation.isPending}
              className="btn-base btn-sm btn-primary flex items-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Atualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Ticket Info Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
              Estado
            </p>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold mt-1.5 border capitalize ${getStatusColor(
                ticket.status
              )}`}
            >
              {ticket.status.replace('_', ' ')}
            </span>
          </div>

          <div>
            <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
              Prioridade
            </p>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold mt-1.5 border capitalize ${getPriorityColor(
                ticket.priority
              )}`}
            >
              {ticket.priority}
            </span>
          </div>

          <div>
            <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
              Categoria
            </p>
            <p className="font-semibold text-slate-800 dark:text-slate-200 mt-1.5 capitalize text-sm">
              {ticket.category === 'technical'
                ? 'Técnico'
                : ticket.category === 'billing'
                  ? 'Faturação'
                  : 'Geral'}
            </p>
          </div>

          <div>
            <p className="text-xs uppercase font-semibold text-slate-400 dark:text-slate-500 tracking-wider">
              Cliente
            </p>
            <Link
              to={`/clients/${ticket.client_id}`}
              className="inline-flex items-center gap-1.5 font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-1.5 text-sm transition-colors"
            >
              <Building2 className="w-4 h-4 text-slate-400" />
              <span>{ticket.client?.company_name || 'Ver Cliente'}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Conversation Thread */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
          <span>Histórico da Conversação</span>
        </h2>

        <div className="space-y-4">
          {ticket.responses && ticket.responses.length > 0 ? (
            ticket.responses.map((resp) => (
              <div
                key={resp.id}
                className={`p-4 rounded-xl border transition-all ${
                  resp.is_staff
                    ? 'ml-6 bg-primary-50/70 dark:bg-primary-950/30 border-primary-200/60 dark:border-primary-800/40'
                    : 'mr-6 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700/60'
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-200">
                    {resp.is_staff ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        <span className="text-primary-700 dark:text-primary-300">
                          Equipa de Suporte (Você)
                        </span>
                      </>
                    ) : (
                      <>
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{ticket.client?.company_name || 'Cliente'}</span>
                      </>
                    )}
                  </span>
                  <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(resp.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>
                <p className="text-slate-800 dark:text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">
                  {resp.message}
                </p>
              </div>
            ))
          ) : (
            <EmptyState
              title="Ainda sem respostas"
              description="Não há respostas nesta conversação ainda."
            />
          )}
        </div>
      </div>

      {/* Response Form */}
      {ticket.status !== 'closed' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
            Adicionar Resposta
          </h2>
          <form onSubmit={handleRespond} className="space-y-4">
            {respondMutation.isError && (
              <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
                <p className="text-sm text-rose-800 dark:text-rose-300">
                  {getErrorMessage(respondMutation.error)}
                </p>
              </div>
            )}

            <textarea
              className="w-full rounded-xl p-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-hidden placeholder-slate-400 transition-all"
              rows={5}
              placeholder="Escreva a sua resposta técnica ou de atendimento aqui..."
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              required
            />

            <button
              type="submit"
              disabled={respondMutation.isPending || !response.trim()}
              className="btn-base btn-md btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {respondMutation.isPending ? (
                <>
                  <span className="spinner"></span>
                  <span>A enviar resposta...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Enviar Resposta ao Cliente</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
